import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import SEOMeta from '@/components/base/SEOMeta';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { homepageFAQs } from '@/mocks/faqs';
import { siteInfo } from '@/mocks/siteInfo';
import { useState } from 'react';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const latestArticles = articles.slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title="91com官方平台-91com最新入口与使用指南"
        description={siteInfo.description}
        keywords="91com,91com官网,91com入口,91com最新地址,91com使用教程,91com注册登录,91com平台介绍"
        canonicalUrl={siteInfo.url}
        ogTitle="91com官方平台-91com最新入口与使用指南"
        ogDescription={siteInfo.description}
      />
      <SchemaOrg pageType="home" faqs={homepageFAQs} />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20orange%20and%20teal%20gradient%20background%20with%20minimal%20geometric%20flowing%20shapes%20soft%20light%20rays%20clean%20modern%20technology%20atmosphere%20Chinese%20website%20hero%20professional%20aesthetic%20subtle%20texture%20elegant%20composition&width=1800&height=900&seq=hero-bg-91com-2026&orientation=landscape"
            alt="91com官方平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 w-full text-center px-4">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
                91com官方平台
              </h1>
              <p className="text-base md:text-lg text-background-200/90 mb-4 max-w-xl mx-auto leading-relaxed">
                91com最新入口与使用指南 — 为您提供安全、快速的官方访问通道
              </p>
              <p className="text-sm text-background-300/80 mb-8 max-w-lg mx-auto">
                广受用户信赖的热门平台，覆盖500万+用户，助您畅享优质在线体验
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="/category/platform-intro"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-compass-3-line mr-2"></i>
                  探索平台功能
                </a>
                <a
                  href="/category/usage-guide"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  查看使用教程
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: siteInfo.usersCount, label: '累计用户', icon: 'ri-user-heart-line' },
                { value: siteInfo.rating, label: '用户评分', icon: 'ri-star-line' },
                { value: siteInfo.reviewsCount, label: '好评数量', icon: 'ri-thumb-up-line' },
                { value: `${articles.length}+`, label: '精品文章', icon: 'ri-article-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                    <span className="text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</span>
                  </div>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                为什么选择<strong>91com</strong>？
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                六大核心优势，为您提供全方位的优质平台体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '采用先进加密技术，全方位保障您的账号安全和数据隐私，让您放心使用。' },
                { icon: 'ri-flashlight-line', title: '极速访问', desc: '优化的服务器架构和CDN加速，确保流畅稳定的访问体验，告别卡顿烦恼。' },
                { icon: 'ri-stack-line', title: '海量资源', desc: '涵盖多个领域的优质内容，持续更新，满足不同用户群体的多样化需求。' },
                { icon: 'ri-customer-service-2-line', title: '贴心服务', desc: '7×24小时在线客服支持，专业团队随时为您解答疑问，解决问题迅速高效。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美支持PC端、移动端和官方APP，随时随地畅享91com平台服务。' },
                { icon: 'ri-group-line', title: '活跃社区', desc: '汇聚数百万用户，分享经验、交流心得，构建温暖互助的社区氛围。' },
              ].map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-lg p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300 group">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                    <i className={`${feature.icon} text-primary-600 group-hover:text-background-50 text-xl transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                内容分类导航
              </h2>
              <p className="text-sm text-foreground-600 max-w-lg mx-auto">
                按主题浏览<strong>91com平台</strong>相关内容，快速找到您需要的信息
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="bg-background-100 rounded-lg p-5 text-center border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-500 transition-colors duration-300 mb-3">
                    <i className={`${cat.icon} text-primary-600 group-hover:text-background-50 text-xl transition-colors duration-300`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500">{cat.articleCount} 篇文章</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">
                  了解<strong>91com</strong>最新动态和使用技巧
                </p>
              </div>
              <Link
                to="/category/latest-news"
                className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/latest-news"
                className="inline-flex items-center gap-1 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative py-14 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20orange%20gradient%20with%20subtle%20geometric%20flowing%20shapes%20clean%20modern%20technology%20atmosphere%20professional%20website%20banner%20background%20elegant%20soft%20lighting%20minimal%20design&width=1800&height=600&seq=cta-bg-91com&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center px-4">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              立即开始您的<strong>91com</strong>之旅
            </h2>
            <p className="text-sm md:text-base text-background-200/90 mb-8 max-w-lg mx-auto">
              加入超过500万用户的行列，探索91com平台的无限可能
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="/category/usage-guide"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-rocket-line mr-2"></i>
                新手指南
              </a>
              <a
                href="/about"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line mr-2"></i>
                了解更多
              </a>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-sm text-foreground-600">
                关于<strong>91com</strong>，您可能想了解的问题都在这里
              </p>
            </div>
            <div className="space-y-3">
              {homepageFAQs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-200/30 transition-colors duration-200"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`text-foreground-500 text-sm transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/faq-help"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看更多常见问题
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}